import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Github, X, ArrowUpRight } from 'lucide-react';
import { projects } from '../data/data';
import { trackEvent } from '../lib/firebase';
import { staggerContainer, fadeInUp } from '../lib/motion';
import { Dialog, DialogContent } from './ui/dialog';

const Projects = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const allProjects = projects;

  const ProjectBlock = ({ project, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}  // ← this is the key fix
      transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1], delay: index * 0.08 }}
      className="group cursor-pointer border border-[#d4d4d0] hover:border-[#8B4513] transition-all duration-300"
      onClick={() => {
        project.details && setSelectedProject(project)

        trackEvent('project_clicked', {
          section_id: project.title,
        })
      }}
      whileHover={{ y: -2, transition: { duration: 0.2 } }}
    >
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-normal text-[#1a1a1a] group-hover:text-[#8B4513] transition-colors duration-300" style={{ fontFamily: 'var(--font-serif)' }}>
            {project.title}
          </h3>
          <ArrowUpRight className="w-4 h-4 text-[#666666] group-hover:text-[#8B4513] opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </div>

        <p className="text-sm text-[#666666] mb-6 leading-relaxed line-clamp-2" style={{ fontFamily: 'var(--font-sans)', fontWeight: 300 }}>
          {project.description}
        </p>

        <div className="flex flex-wrap gap-2 mb-4">
          {project.tech.slice(0, 3).map((tech, idx) => (
            <span
              key={idx}
              className="text-[10px] text-[#666666] uppercase tracking-widest border border-[#d4d4d0] px-2 py-1"
              style={{ fontFamily: 'var(--font-sans)' }}
            >
              {tech}
            </span>
          ))}
        </div>

        <div className="flex gap-4 text-xs pt-4 border-t border-[#d4d4d0]">
          {project.github && (
            <a
              href={project.github}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 text-[#666666] hover:text-[#8B4513] transition-colors duration-300 uppercase tracking-wide"
              style={{ fontFamily: 'var(--font-sans)' }}
            >
              <Github className="w-3 h-3" />
              Code
            </a>
          )}
          {project.demo && (
            <a
              href={project.demo}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 text-[#666666] hover:text-[#8B4513] transition-colors duration-300 uppercase tracking-wide"
              style={{ fontFamily: 'var(--font-sans)' }}
            >
              <ExternalLink className="w-3 h-3" />
              Visit
            </a>
          )}
          {!project.github && !project.demo && (
            <span
              className="text-[#666666] uppercase tracking-wide"
              style={{ fontFamily: 'var(--font-sans)' }}
            >
              Private repo
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );

  return (
    <section id="projects" className="py-24 bg-[#f8f8f6] text-black">
      <div className="container-editorial">
        <div className="grid grid-cols-12 gap-y-8 lg:gap-x-8 mb-16">
          <div className="col-span-12 lg:col-span-4">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7 }}
            >
              <p className="text-xs uppercase tracking-widest text-[#666666] mb-4" style={{ fontFamily: 'var(--font-sans)' }}>
                Selected Work
              </p>
              <h2 className="text-5xl font-normal text-[#1a1a1a]" style={{ fontFamily: 'var(--font-serif)' }}>
                Projects
              </h2>
            </motion.div>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7 flex items-end">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-sm text-[#666666] leading-relaxed"
              style={{ fontFamily: 'var(--font-sans)', fontWeight: 300 }}
            >
              A curated selection of projects spanning infrastructure, full-stack development, and developer tooling.
            </motion.p>
          </div>
        </div>

        {/* All Projects in Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: "-100px" }}
        >
          {allProjects.map((project, index) => (
            <ProjectBlock key={project.id} project={project} index={index} />
          ))}
        </motion.div>
      </div>

      {/* Project Details Modal */}
      <Dialog open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
        <DialogContent className="max-w-3xl bg-[#f8f8f6] border border-[#d4d4d0] p-0">
          {selectedProject && (
            <motion.div
              className="p-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              {/* <button
                onClick={() => setSelectedProject(null)}
                className="absolute right-8 top-8 text-[#666666] hover:text-[#1a1a1a] transition-colors"
              >
                <X className="w-5 h-5" />
              </button> */}

              <h2 className="text-4xl font-normal text-[#1a1a1a] mb-6" style={{ fontFamily: 'var(--font-serif)' }}>
                {selectedProject.title}
              </h2>

              {/* <div className="aspect-[16/9] bg-[#e8e8e4] mb-8">
                <div className="w-full h-full flex items-center justify-center text-[#666666]" style={{ fontFamily: 'var(--font-sans)' }}>
                  {selectedProject.title}
                </div>
              </div> */}

              <p className="text-lg text-[#666666] leading-relaxed mb-8" style={{ fontFamily: 'var(--font-sans)', fontWeight: 300 }}>
                {selectedProject.details}
              </p>

              <div className="flex flex-wrap gap-3 mb-8">
                {selectedProject.tech.map((tech, idx) => (
                  <span
                    key={idx}
                    className="text-xs text-[#666666] uppercase tracking-wider border border-[#d4d4d0] px-3 py-1"
                    style={{ fontFamily: 'var(--font-sans)' }}
                  >
                    {tech}
                  </span>
                ))}
              </div>

              <div className="flex gap-6 pt-6 border-t border-[#d4d4d0]">
                {selectedProject.github && (
                  <a
                    href={selectedProject.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#1a1a1a] hover:text-[#8B4513] transition-colors duration-300 uppercase tracking-wide text-sm"
                    style={{ fontFamily: 'var(--font-sans)' }}
                  >
                    <Github className="w-4 h-4" />
                    View Code
                  </a>
                )}
                {selectedProject.demo && (
                  <a
                    href={selectedProject.demo}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#1a1a1a] hover:text-[#8B4513] transition-colors duration-300 uppercase tracking-wide text-sm"
                    style={{ fontFamily: 'var(--font-sans)' }}
                  >
                    <ExternalLink className="w-4 h-4" />
                    Visit Project
                  </a>
                )}
              </div>
            </motion.div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Projects;